/**
 * A voz escolhida, e onde ela vive.
 *
 * ## Por que existe
 *
 * A voz do Will era `OMNIVOICE_DEFAULT_VOICE` num arquivo de ambiente do
 * servidor: trocar exigia editar `/etc/will-voice.env`, reiniciar o serviço e
 * ter acesso à máquina. Comparar duas vozes significava fazer isso duas vezes,
 * e comparar a mesma voz em dois estados afetivos, quatro. Na prática ninguém
 * comparava — a voz padrão era a que estava lá desde o primeiro dia.
 *
 * Aqui a escolha passa a ser do navegador, persistida, e lida pelo caminho de
 * fala de verdade. A do servidor continua sendo o padrão de quem nunca
 * escolheu, o que é o comportamento certo: uma instalação nova tem voz.
 *
 * ## O que NÃO muda por aqui
 *
 * A **identidade** continua sendo uma decisão de produto: uma voz principal,
 * um `mode`. Este módulo é a bancada que permite tomar essa decisão ouvindo, e
 * não a licença para cada tela ter a sua. Por isso a escolha é uma só, global,
 * e não por atividade — o que varia por atividade é a prosódia, que é a camada
 * de pontuação no servidor.
 */

const CHAVE = 'will.u1.voz';

export interface EscolhaDeVoz {
  /** id no catálogo do OmniVoice, ou `null` para usar o padrão do servidor */
  vozId: string | null;
  /** modo de inferência; `null` usa o do servidor */
  modo: string | null;
  /**
   * Idioma da fala (`pt-BR`, `en-US`…). `null` usa o padrão do servidor.
   *
   * É escolha de produto e não de turno: o Will tem UMA língua padrão, do mesmo
   * jeito que tem uma voz. Deixar o idioma variar por resposta seria a versão
   * linguística do defeito que o Márcio ouviu — a identidade trocando conforme
   * a função.
   */
  idioma: string | null;
}

const VAZIA: EscolhaDeVoz = { vozId: null, modo: null, idioma: null };

/** Avisados quando a escolha muda — o app reage sem recarregar a página. */
const ouvintes = new Set<(e: EscolhaDeVoz) => void>();

let atual: EscolhaDeVoz | null = null;

export function escolhaAtual(): EscolhaDeVoz {
  if (atual !== null) return atual;
  try {
    const cru = window.localStorage.getItem(CHAVE);
    if (cru !== null) {
      const dado: unknown = JSON.parse(cru);
      if (typeof dado === 'object' && dado !== null) {
        const d = dado as Record<string, unknown>;
        atual = {
          vozId: typeof d['vozId'] === 'string' ? d['vozId'] : null,
          modo: typeof d['modo'] === 'string' ? d['modo'] : null,
          idioma: typeof d['idioma'] === 'string' ? d['idioma'] : null,
        };
        return atual;
      }
    }
  } catch {
    // localStorage bloqueado (modo privado, iframe): a escolha vira volátil.
    // Perder a persistência é aceitável; quebrar a voz por causa dela, não.
  }
  atual = VAZIA;
  return atual;
}

export function escolher(nova: Partial<EscolhaDeVoz>): EscolhaDeVoz {
  const combinada: EscolhaDeVoz = { ...escolhaAtual(), ...nova };
  atual = combinada;
  try {
    window.localStorage.setItem(CHAVE, JSON.stringify(combinada));
  } catch {
    /* ver acima */
  }
  for (const cb of ouvintes) cb(combinada);
  return combinada;
}

export function aoTrocarVoz(cb: (e: EscolhaDeVoz) => void): () => void {
  ouvintes.add(cb);
  return () => ouvintes.delete(cb);
}

/** Volta ao padrão do servidor — o botão de "desfazer" da bancada. */
export function usarPadraoDoServidor(): EscolhaDeVoz {
  return escolher({ vozId: null, modo: null, idioma: null });
}

/* ── Catálogo ────────────────────────────────────────────────────────────── */

export interface VozDoCatalogo {
  id: string;
  rotulo: string;
  feitio: string;
  /** latência medida com a mesma frase, cache furado */
  msMedido: number;
}

export interface ModoDoCatalogo {
  id: string;
  /**
   * Se este modo produz áudio diferente dos outros.
   *
   * Substituiu o `msMedido` por modo, que afirmava uma diferença que a medição
   * não sustenta: as cinco medianas cabem em 58 ms, contra uma cauda de ~1.250 ms
   * dentro de um único modo. Pior — `balanced`, `precision`, `quality` e `batch`
   * devolvem o MESMO arquivo, byte a byte. Só `realtime` muda o áudio.
   *
   * Mostrar um número por modo convidava a escolher pelo que não muda nada.
   *
   * É este campo — e só ele — que autoriza a tela a reduzir os cinco nomes às
   * duas coisas que existem para ouvir. A redução mora em `modos.ts`; ausente o
   * campo, ela não acontece, porque a dicotomia deixaria de ser medida e
   * passaria a ser suposta.
   */
  audioDistinto?: boolean;
  aviso?: string;
}

export interface IdiomaDoCatalogo {
  id: string;
  rotulo: string;
}

export interface Catalogo {
  padrao: string;
  modoPadrao: string;
  idiomaPadrao: string;
  idiomas: IdiomaDoCatalogo[];
  vozes: VozDoCatalogo[];
  modos: ModoDoCatalogo[];
}

let catalogoEmVoo: Promise<Catalogo | null> | null = null;

/**
 * Busca o catálogo no servidor. Uma vez por carga da página.
 *
 * Não tem lista de reserva no cliente de propósito: uma segunda lista de vozes
 * aqui divergiria da do servidor no primeiro dia, e a bancada existe justamente
 * para dizer a verdade sobre o que está instalado.
 */
export function carregarCatalogo(): Promise<Catalogo | null> {
  catalogoEmVoo ??= fetch('/api/tts/voices', { headers: { accept: 'application/json' } })
    .then(async (r) => (r.ok ? ((await r.json()) as Catalogo) : null))
    .catch(() => null);
  return catalogoEmVoo;
}
